import {
	getInvestmentWorkflowByTsaId,
	updateInvestmentWorkflow,
} from '@dealstack/frontend/shared/dataExchange/services/god.service';
import { useDSToast } from '@dealstack/frontend/shared/mechanism/toast/useDSToast';
import { UpdateShareAssociation } from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import { useCommonYupValidation } from '@dealstack/frontend/shared/other/react/form/useCommonYupValidation';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

interface IManageInvestmentWorkflowController {
	investmentWorkflowId: string;
}

interface IDefaultValues {
	[key: string]: number;
}

interface IValidationObject {
	[key: string]: Yup.NumberSchema;
}

export const useManageInvestmentWorkflowModal = ({
	investmentWorkflowId,
}: IManageInvestmentWorkflowController) => {
	const toastAPI = useDSToast();
	const commonYup = useCommonYupValidation();
	const [showModal, setShowModal] = useState(false);
	const [validationObject, setValidationObject] = useState<IValidationObject>({});

	const defaultValues = {} as IDefaultValues;

	const getInvestmentWorkflowRequest = useQuery({
		queryKey: [
			formatUnicorn(
				API_PATHS.GODMODE_TRANSACTION_SHARE_ASSOCIATION_INVESTMENTWORKFLOWID_GET,
				{
					investmentWorkflowId,
				}
			),
		],

		queryFn: ({ signal }) =>
			getInvestmentWorkflowByTsaId(
				formatUnicorn(
					API_PATHS.GODMODE_TRANSACTION_SHARE_ASSOCIATION_INVESTMENTWORKFLOWID_GET,
					{
						investmentWorkflowId,
					}
				),
				signal
			),

		enabled: !!investmentWorkflowId,
		meta: { suppressErrorToast: true },
	});
	const shareAssociations = getInvestmentWorkflowRequest?.data?.payload;
	const validationSchema = useMemo(() => {
		if (!validationObject) {
			return;
		}
		return Yup.object().shape(validationObject);
	}, [validationObject]);

	const createInvestmentWorkflowForm = useForm({
		mode: 'all',
		reValidateMode: 'onChange',
		resolver: yupResolver(validationSchema),
		defaultValues,
	});

	createInvestmentWorkflowForm.watch();

	useEffect(() => {
		if (!shareAssociations) {
			return;
		}
		for (const shareAssociation of shareAssociations) {
			defaultValues[`${shareAssociation.shareAssociationId}_number`] =
				shareAssociation.number;
			defaultValues[`${shareAssociation.shareAssociationId}_cost`] = shareAssociation.cost;

			setValidationObject((prevState) => ({
				...prevState,
				[`${shareAssociation.shareAssociationId}_number`]:
					commonYup.number.required.typeError('Share amount must be a number'),
				[`${shareAssociation.shareAssociationId}_cost`]:
					commonYup.number.required.typeError('Cost must be a number'),
			}));
		}
		createInvestmentWorkflowForm.reset(defaultValues);
	}, [shareAssociations?.length, showModal]);

	const updateInvestmentWorkflowRequest = useMutation({
		mutationFn: updateInvestmentWorkflow,
	});
	const onSubmit = useCallback((formData: IDefaultValues) => {
		const groupedData = Object.entries(formData).reduce((acc, [key, value]) => {
			const [shareAssociationId, field] = key.split('_');
			const existingData = acc.find((each) => each.shareAssociationId === shareAssociationId);
			if (existingData?.shareAssociationId) {
				existingData[field] = value;
				return acc;
			}
			acc.push({
				shareAssociationId,
				[field]: value,
			});
			return acc;
		}, [] as UpdateShareAssociation[]);

		updateInvestmentWorkflowRequest.mutate(
			{
				updateShareAssociations: groupedData,
			},
			{
				onSuccess: () => {
					toastAPI.success({
						title: 'Investor Investment Data',
						message: 'Updated investment successfully',
					});
					setShowModal(false);
					void getInvestmentWorkflowRequest.refetch();
				},
			}
		);
	}, []);
	return {
		createInvestmentWorkflowForm,
		onSubmit,
		shareAssociations,
		setShowModal,
		showModal,
		isLoading: getInvestmentWorkflowRequest?.isLoading,
	};
};
